import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Eye, EyeOff, Download, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { usersApi } from '../../utils/api';

const PrivacySettings = () => {
  const { user, setUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [profileVisibility, setProfileVisibility] = useState(true);
  const [showPublishedSkills, setShowPublishedSkills] = useState(true);
  const [usageAnalytics, setUsageAnalytics] = useState(true);

  useEffect(() => {
    if (user) {
      setProfileVisibility(user.profile_visibility ?? true);
      setShowPublishedSkills(user.show_published_skills ?? true);
      setUsageAnalytics(user.usage_analytics ?? true);
    }
  }, [user]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await usersApi.updateSettings({
        profile_visibility: profileVisibility,
        show_published_skills: showPublishedSkills,
        usage_analytics: usageAnalytics,
      });

      setUser({
        ...user,
        profile_visibility: profileVisibility,
        show_published_skills: showPublishedSkills,
        usage_analytics: usageAnalytics,
      });

      toast.success('Privacy settings updated successfully');
    } catch (error) {
      console.error('Failed to save privacy settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestDataExport = async () => {
    try {
      await usersApi.requestDataExport();
      toast.success('Data export requested. You will receive an email when ready.');
    } catch (error) {
      console.error('Failed to request data export:', error);
      toast.error(error.response?.data?.detail || 'Failed to request data export');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-text-dark dark:text-dark-text mb-2">Privacy</h2>
        <p className="text-text-gray dark:text-dark-text-secondary">
          Control your privacy and data settings
        </p>
      </div>

      {/* Profile Visibility Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Eye className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">Profile Visibility</h3>
        </div>

        <div className="space-y-4 max-w-2xl">
          <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-[#2e2e2d] rounded-lg">
            <div className="space-y-0.5">
              <Label>Public Profile</Label>
              <p className="text-sm text-text-gray dark:text-dark-text-secondary">
                Make your profile visible to everyone on Skillerate
              </p>
            </div>
            <Switch
              checked={profileVisibility}
              onCheckedChange={setProfileVisibility}
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-[#2e2e2d] rounded-lg">
            <div className="space-y-0.5">
              <Label>Show Published Skills</Label>
              <p className="text-sm text-text-gray dark:text-dark-text-secondary">
                Display your published skills on your profile
              </p>
            </div>
            <Switch
              checked={showPublishedSkills}
              onCheckedChange={setShowPublishedSkills}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Data & Analytics Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">Data & Analytics</h3>
        </div>

        <div className="max-w-2xl">
          <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-[#2e2e2d] rounded-lg">
            <div className="space-y-0.5">
              <Label>Usage Analytics</Label>
              <p className="text-sm text-text-gray dark:text-dark-text-secondary">
                Help us improve Skillerate by sharing anonymous usage data
              </p>
            </div>
            <Switch
              checked={usageAnalytics}
              onCheckedChange={setUsageAnalytics}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Data Export Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Download className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">Data Export</h3>
        </div>

        <div className="max-w-2xl">
          <div className="p-4 border border-gray-200 dark:border-[#2e2e2d] rounded-lg">
            <p className="font-medium text-text-dark dark:text-dark-text mb-2">
              Request Your Data
            </p>
            <p className="text-sm text-text-gray dark:text-dark-text-secondary mb-4">
              Download a copy of your account data, including your profile, skills, and activity.
            </p>
            <Button variant="outline" onClick={handleRequestDataExport}>
              <Download className="h-4 w-4 mr-2" />
              Request Data Export
            </Button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-4">
        <Button variant="gradient" onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default PrivacySettings;
